import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { speakText } from '../utils/audioUtils';
import { supabase } from '../supabase';
import BottomNav from '../components/BottomNav';
import LessonBannerAd from '../components/LessonBannerAd';
import './ConversationDetailScreen.css';

interface Conversation {
  id: string;
  topic_italian: string;
  topic_bengali: string;
  topic_english: string;
  description_italian?: string;
  description_bengali?: string;
  description_english?: string;
  category?: string;
  icon?: string;
}

interface ConversationMessage {
  id: string;
  speaker: string;
  message_italian: string;
  message_bengali: string;
  message_english: string;
  audio_url?: string;
  order_index: number;
}

const ConversationDetailScreen: React.FC = () => {
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId: string }>();
  const { currentLanguage } = useLanguage();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);

  const content = {
    bn: {
      back: "ফিরে যান",
      loading: "লোড হচ্ছে...",
      noConversation: "কথোপকথন পাওয়া যায়নি"
    },
    it: {
      back: "Indietro",
      loading: "Caricamento...",
      noConversation: "Conversazione non trovata"
    },
    en: {
      back: "Back",
      loading: "Loading...",
      noConversation: "Conversation not found"
    }
  };

  useEffect(() => {
    if (conversationId) {
      loadConversation();
    }
  }, [conversationId]);

  const loadConversation = async () => {
    try {
      setLoading(true);

      // Load conversation
      const { data: conversationData, error: conversationError } = await supabase
        .from('conversations')
        .select('*')
        .eq('id', conversationId)
        .single();

      if (conversationError) {
        console.error('Error loading conversation:', conversationError);
        setLoading(false);
        return;
      }

      setConversation(conversationData);

      // Load messages
      const { data: messagesData, error: messagesError } = await supabase
        .from('conversation_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('order_index', { ascending: true });

      if (messagesError) {
        console.error('Error loading messages:', messagesError);
      } else {
        setMessages(messagesData || []);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMessageClick = async (message: ConversationMessage) => {
    try {
      setPlayingAudio(message.id);
      await speakText(message.message_italian, 'answer');
    } catch (error) {
      console.error('Error speaking:', error);
    } finally {
      setTimeout(() => setPlayingAudio(null), 1000);
    }
  };

  const getTopicName = () => {
    if (!conversation) return '';
    if (currentLanguage === 'it') return conversation.topic_italian;
    if (currentLanguage === 'bn') return conversation.topic_bengali;
    return conversation.topic_english;
  };

  const getMessageText = (message: ConversationMessage) => {
    if (currentLanguage === 'it') return message.message_italian;
    if (currentLanguage === 'bn') return message.message_bengali;
    return message.message_english;
  };

  if (loading) {
    return (
      <div className="conversation-detail-screen">
        <div className="screen-header">
          <button 
            className="header-btn back-btn"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            ←
          </button>
          <div className="header-center">
            <h1>{content[currentLanguage].loading}</h1>
          </div>
          <div className="header-btn"></div>
        </div>
        <div className="loading-container">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <p>{content[currentLanguage].loading}</p>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="conversation-detail-screen">
        <div className="screen-header">
          <button 
            className="header-btn back-btn"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            ←
          </button>
          <div className="header-center">
            <h1>{content[currentLanguage].noConversation}</h1>
          </div>
          <div className="header-btn"></div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="conversation-detail-screen">
      {/* Header */}
      <div className="screen-header">
        <button 
          className="header-btn back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="header-center">
          <h1>{getTopicName()}</h1>
        </div>
        <button 
          className="header-btn settings-btn"
          onClick={() => navigate('/settings')}
          aria-label="Open settings"
        >
          ⋯
        </button>
      </div>

      <LessonBannerAd />

      {/* Conversation Messages */}
      <div className="conversation-messages-container">
        {messages.length === 0 ? (
          <div className="no-messages">
            <p>No messages found</p>
          </div>
        ) : (
          <div className="conversation-messages-list">
            {messages.map((message, index) => {
              const isEven = index % 2 === 0;
              const isPlaying = playingAudio === message.id;

              return (
                <div
                  key={message.id}
                  className={`message-bubble ${isEven ? 'message-left' : 'message-right'} ${isPlaying ? 'playing' : ''}`}
                  onClick={() => handleMessageClick(message)}
                >
                  <div className="message-speaker">{message.speaker}</div>
                  <div className="message-content">
                    <div className="message-italian">{message.message_italian}</div>
                    <div className="message-translation">{getMessageText(message)}</div>
                  </div>
                  <button 
                    className="message-audio-btn" 
                    aria-label="Play audio"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMessageClick(message);
                    }}
                  >
                    🔊
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default ConversationDetailScreen;

